import React, { useCallback, useEffect, useState, useMemo } from 'react'
import _isEmpty from 'lodash/isEmpty'
import { useIntl } from 'react-intl'
import { Formik, FormikHelpers, FormikProps } from 'formik'
import * as Yup from 'yup'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { LICENSES_STORE_URL } from 'app/consts'
import { CommonSearch } from 'components/Table'
import usePermissions from 'features/auth/permissions/usePermissions'
import {
  NON_DIRECT_DEALER,
  ROOT_ADMIN,
  ROOT_USER,
} from 'features/auth/permissions/consts'

import { makePublicUrl } from 'app/utils/publicUrl'
import {
  fetchLicensesListThunk,
  importLicenseThunk,
  licensesListActions,
} from './slice'
import LicensesGroupsTable from './components/LicensesGroupsTable'
import { SpinnerCentered } from 'components/Loader'
import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'
import { commonMessages } from 'app/i18n/messages'

import {
  currentRootDomainIdSelector,
  rootDomainByIdSelector,
  selectYouAreDistributor,
  selectDomainDealers,
} from 'features/rootDomains/selectors'

import { fetchDomainDealersThunk } from 'features/domains/slice'
import {
  licensesListErrorSelector,
  licensesListIsFetchingSelector,
  licensesListIsFinishedSelector,
  licensesGroupsSelector,
} from './selectors'
import {
  StyledNoLicenses,
  StyledLicensesContainer,
  StyledLicensesHeader,
  VisitStoreButton,
  LicensesToggleButtonsWrapper,
  LicensesToggleButton,
  LicensesSearchWrapper,
  LicensesHeaderTitle,
  ImportLicenseButton,
  ImportLicenseForm,
  LicensesFilterWrapper,
  LicensesFilterItem,
  LicensesFilterItemTitle,
} from './styles'

import Checkbox from 'components/Input/Checkbox'
import { homeLicensesMessages } from './messages'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { TextField } from 'components/Fields'
import NotFoundBlock from 'components/NotFoundBlock'
import { ImportLicensePayload } from './api'
import { LICENSES_IMPORT_FILTERS, LicenseType } from './types'
import { licenseFiltersMap } from './utils'

const notFoundImage = makePublicUrl('images/illustrations/notFound.svg')

interface ImportLicenseValues {
  code: string
}

// TODO: add localization
function Licenses() {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const currentRootDomainId = useAppSelector(currentRootDomainIdSelector)
  const currentRootDomain = useAppSelector(
    rootDomainByIdSelector(currentRootDomainId)
  )
  const licensesGroups = useAppSelector(licensesGroupsSelector)
  const isFetching = useAppSelector(licensesListIsFetchingSelector)
  const isFinished = useAppSelector(licensesListIsFinishedSelector)
  const isError = useAppSelector(licensesListErrorSelector)
  const youAreDistributor = useAppSelector(selectYouAreDistributor)
  const domainDealers = useAppSelector(selectDomainDealers.data)

  const [hasRootUserPermission] = usePermissions({ allow: [ROOT_USER] })
  const [hasRootAdminPermission] = usePermissions({ allow: [ROOT_ADMIN] })
  const [isNonDirectDealer] = usePermissions({ allow: [NON_DIRECT_DEALER] })

  const [showAvailableCodes, setShowAvailableCodes] = useState(true)
  const [searchValue, setSearchValue] = useState<string>()
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [licensesFilters, setLicensesFilters] = useState({
    [LICENSES_IMPORT_FILTERS.transferred]: false,
    [LICENSES_IMPORT_FILTERS.rma]: false,
  })

  const licensesGroupsWithDomainName = useMemo(
    () =>
      youAreDistributor && domainDealers
        ? licensesGroups.map((group) =>
            group.map((license) => {
              const dealer = domainDealers.find(
                (dealer) => dealer.rootDomainId === license.domainId
              )
              const transferredLicense =
                license.domainId &&
                license.domainId !== currentRootDomainId &&
                license.purchaserDomainId === currentRootDomainId
              const canRenderDomainName =
                transferredLicense && youAreDistributor
              const domainName = (canRenderDomainName && dealer?.name) || ''
              return { ...license, domainName }
            })
          )
        : licensesGroups,
    [youAreDistributor, licensesGroups, domainDealers, currentRootDomainId]
  )

  const [filteredLicenseGroup, setFilteredLicenseGroup] = useState(
    licensesGroupsWithDomainName
  )

  const importValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        code: Yup.string().required('License Code is required'),
      }),
    []
  )

  const initialImportValues: ImportLicenseValues = {
    code: '',
  }

  const handleImportDiscard = (
    formikProps: FormikProps<ImportLicenseValues>
  ) => {
    formikProps.resetForm()
    setIsImportOpen(false)
  }

  const handleImportSubmit = async (
    values: ImportLicenseValues,
    {
      setSubmitting,
      setStatus,
      setFieldError,
    }: FormikHelpers<ImportLicenseValues>
  ) => {
    try {
      const importLicenseData: ImportLicensePayload = {
        domainId: currentRootDomainId || '',
        code: values.code,
      }
      await dispatch(importLicenseThunk(importLicenseData))
      await dispatch(
        fetchLicensesListThunk(currentRootDomainId || '', {
          applied: false,
          relevant: true,
          withCapabilities: false,
        })
      )
      setStatus({ success: true, error: false })
      setIsImportOpen(false)
    } catch (e: any) {
      console.log('Error while importing license', e)
      setFieldError('code', e.message)
      setStatus({ success: false, error: true })
    } finally {
      setSubmitting(false)
    }
  }

  const handleShowAvailableCodes = useCallback(
    () => setShowAvailableCodes(true),
    []
  )
  const handleHideAvailableCodes = useCallback(
    () => setShowAvailableCodes(false),
    []
  )

  useEffect(() => {
    const filters = []
    let filteredValues = licensesGroupsWithDomainName

    if (licensesFilters[LICENSES_IMPORT_FILTERS.rma]) {
      filters.push(LICENSES_IMPORT_FILTERS.rma)
    }

    if (licensesFilters[LICENSES_IMPORT_FILTERS.transferred]) {
      filters.push(LICENSES_IMPORT_FILTERS.transferred)
    }

    filters.forEach((filter: LICENSES_IMPORT_FILTERS) => {
      filteredValues = filteredValues.reduce((prev, group: LicenseType[]) => {
        const filterGroup = group.filter((license: LicenseType) => {
          return licenseFiltersMap[filter](currentRootDomainId, license)
        })
        return filterGroup.length ? [...prev, filterGroup] : prev
      }, [])
    })

    setFilteredLicenseGroup(filteredValues)
  }, [currentRootDomainId, licensesFilters, licensesGroupsWithDomainName])

  useEffect(() => {
    if (currentRootDomainId) {
      dispatch(
        fetchLicensesListThunk(currentRootDomainId, {
          applied: !showAvailableCodes,
          relevant: true,
          withCapabilities: false,
        })
      )
      dispatch(fetchDomainDealersThunk(currentRootDomainId))
    }

    return () => {
      dispatch(licensesListActions.clear())
    }
  }, [dispatch, currentRootDomainId, showAvailableCodes])

  const handleChangeCheckboxFilter = useCallback(
    (fieldName: LICENSES_IMPORT_FILTERS) =>
      (input: React.ChangeEvent<HTMLInputElement>) => {
        setLicensesFilters((prev) => ({
          ...prev,
          [fieldName]: input.target.checked,
        }))
      },
    []
  )

  const isAppliedLicensesFilters =
    licensesFilters[LICENSES_IMPORT_FILTERS.rma] ||
    licensesFilters[LICENSES_IMPORT_FILTERS.transferred]

  return (
    <StyledLicensesContainer>
      <StyledLicensesHeader>
        <LicensesHeaderTitle $hasExtraBtn={isNonDirectDealer}>
          {formatMessage(homeLicensesMessages.licenses)}
        </LicensesHeaderTitle>

        <LicensesToggleButtonsWrapper>
          <LicensesToggleButton
            isActive={showAvailableCodes}
            onClick={handleShowAvailableCodes}
          >
            {formatMessage(homeLicensesMessages.availableCodes)}
          </LicensesToggleButton>
          <LicensesToggleButton
            isActive={!showAvailableCodes}
            onClick={handleHideAvailableCodes}
          >
            {formatMessage(homeLicensesMessages.usedCodes)}
          </LicensesToggleButton>
        </LicensesToggleButtonsWrapper>

        {isNonDirectDealer && (
          <>
            <ImportLicenseButton
              variant="secondary"
              onClick={() => setIsImportOpen(true)}
            >
              {formatMessage(homeLicensesMessages.importLicense)}
            </ImportLicenseButton>

            <Formik
              initialValues={initialImportValues}
              validationSchema={importValidationSchema}
              onSubmit={handleImportSubmit}
            >
              {(formikProps) => {
                return (
                  <ConfirmModal
                    title={formatMessage(homeLicensesMessages.importLicense)}
                    isOpen={isImportOpen}
                    onDiscard={() => handleImportDiscard(formikProps)}
                    discardBtnTitle={formatMessage(commonMessages.cancel)}
                    confirmBtnTitle={formatMessage(commonMessages.submit)}
                    variant="primary"
                    onConfirm={formikProps.submitForm}
                    isConfirmBtnDisabled={
                      formikProps.isSubmitting ||
                      !formikProps.isValid ||
                      !formikProps.values.code
                    }
                  >
                    <ImportLicenseForm>
                      <TextField
                        name="code"
                        placeholder={formatMessage(
                          homeLicensesMessages.licenseCode
                        )}
                        showLabel
                      />
                    </ImportLicenseForm>
                  </ConfirmModal>
                )
              }}
            </Formik>
          </>
        )}

        <VisitStoreButton
          as="a"
          href={LICENSES_STORE_URL}
          target="_blank"
          type="button"
          variant="secondary"
        >
          {formatMessage(homeLicensesMessages.visitStore)}
        </VisitStoreButton>
      </StyledLicensesHeader>

      <LicensesSearchWrapper>
        <CommonSearch
          setSearchValue={setSearchValue}
          placeholder={formatMessage(homeLicensesMessages.searchPlaceholder)}
        />

        {youAreDistributor && (
          <LicensesFilterWrapper>
            <LicensesFilterItem>
              <Checkbox
                checked={licensesFilters[LICENSES_IMPORT_FILTERS.transferred]}
                onChange={handleChangeCheckboxFilter(
                  LICENSES_IMPORT_FILTERS.transferred
                )}
              />
              <LicensesFilterItemTitle>Transferred</LicensesFilterItemTitle>
            </LicensesFilterItem>
            <LicensesFilterItem>
              <Checkbox
                checked={licensesFilters[LICENSES_IMPORT_FILTERS.rma]}
                onChange={handleChangeCheckboxFilter(
                  LICENSES_IMPORT_FILTERS.rma
                )}
              />
              <LicensesFilterItemTitle>RMA</LicensesFilterItemTitle>
            </LicensesFilterItem>
          </LicensesFilterWrapper>
        )}
      </LicensesSearchWrapper>

      {isFetching && <SpinnerCentered />}

      {isError && (
        <StyledNoLicenses>
          {formatMessage(commonMessages.commonError)}
        </StyledNoLicenses>
      )}

      {isFinished &&
        _isEmpty(filteredLicenseGroup) &&
        !isError &&
        (isAppliedLicensesFilters ? (
          <NotFoundBlock
            imgSrc={notFoundImage}
            title="No Licenses Found"
            text="Try to change your search or filter criteria"
          />
        ) : (
          <StyledNoLicenses>
            {formatMessage(
              showAvailableCodes
                ? homeLicensesMessages.purchasedLicenses
                : homeLicensesMessages.appliedLicenses,
              { domainName: currentRootDomain?.name || '' }
            )}
          </StyledNoLicenses>
        ))}

      {isFinished && !_isEmpty(filteredLicenseGroup) && !isError && (
        <CurrentUserPermissionsContext.Provider
          value={{ hasRootUserPermission, hasRootAdminPermission }}
        >
          <LicensesGroupsTable
            licensesGroups={filteredLicenseGroup}
            searchValue={searchValue}
            isAppliedLicensesFilters={isAppliedLicensesFilters}
            showAvailableCodes={showAvailableCodes}
          />
        </CurrentUserPermissionsContext.Provider>
      )}
    </StyledLicensesContainer>
  )
}

export default Licenses
